import axios, { AxiosError, AxiosResponse, AxiosRequestConfig } from 'axios';
import { getAccessToken, setAccessToken } from './tokenStore';
import { msalInstance } from '../index';
import { getApiBaseUrl } from '../utils/apiUtils';
import { refreshAndStoreToken } from '../utils/tokenRefresh';
import { IPropertyAddressItem } from '../constants/interfaces';

// Define our own request config interface with retry flag
interface RequestConfig extends AxiosRequestConfig {
  _isRetry?: boolean;
}

const apiBaseUrl = getApiBaseUrl();

const api = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

api.interceptors.request.use(
  async (config) => {
    let token = getAccessToken();
    if (!token && msalInstance) {
      // Try to refresh and store token
      await refreshAndStoreToken(msalInstance);
      token = getAccessToken();
    }

    if (token) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      config.headers!.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest: RequestConfig = error.config as RequestConfig;

    // Check if this is a 401 error and the request hasn't been retried yet
    if (error.response?.status === 401 && !originalRequest._isRetry) {
      // Mark this request as being retried to prevent infinite loops
      originalRequest._isRetry = true;

      setAccessToken('');

      if (msalInstance) {
        // Try to refresh token using utility
        const success = await refreshAndStoreToken(msalInstance);

        if (success) {
          // Get the refreshed token and retry the request
          const token = getAccessToken();
          if (token) {
            if (!originalRequest.headers) {
              originalRequest.headers = {};
            }
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          }
        }

        // Token refresh failed - force login
        console.error('Token refresh failed - forcing login');
        await msalInstance.loginRedirect({
          scopes: ['User.Read'],
        });
      } else {
        // Fallback if msalInstance is not available
        window.location.href = '/';
      }

      return Promise.reject(error);
    }

    // If this is a second 401 (retry failed) or any other error, reject it
    return Promise.reject(error);
  }
);

export async function apiRequest<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
  return api.request<T>({ url, ...config });
}

/**
 * Fetches payoff amount for a given account ID
 * @param accountId - The account ID to fetch payoff amount for
 * @returns Promise with payoff amount response
 */
export async function fetchPayoffAmount(
  accountId: string
): Promise<AxiosResponse<{ accountId: string; payoffAmount: number }>> {
  return apiRequest<{ accountId: string; payoffAmount: number }>(
    `/api/Account/payoff/${accountId}`,
    {
      method: 'GET',
    }
  );
}

/**
 * Fetches property addresses for a given account ID
 * @param accountId - The servicer account ID to fetch property addresses for
 * @returns Promise with property addresses response
 */
export async function fetchPropertyAddresses(accountId: string): Promise<
  AxiosResponse<{
    accountId: string;
    addresses: IPropertyAddressItem[];
  }>
> {
  return apiRequest<{
    accountId: string;
    addresses: IPropertyAddressItem[];
  }>(`/api/Account/property-addresses/${encodeURIComponent(accountId)}`, {
    method: 'GET',
  });
}

export default api;
